import { useState, useEffect, useCallback } from 'react';
import localCacheService from '../services/localCacheService';

/**
 * useCacheStatus — 本地缓存状态管理
 * 从 EditorContent 提取的 3 个 useState（cacheInvalid, cacheReconfiguring, showCacheConfirm）
 * + 缓存监控 useEffect + 验证逻辑
 */
export default function useCacheStatus() {
  const [cacheInvalid, setCacheInvalid] = useState(false);
  const [cacheReconfiguring, setCacheReconfiguring] = useState(false);
  const [showCacheConfirm, setShowCacheConfirm] = useState(false);

  // 缓存监控：页面可见性变化 + cache-config-changed 事件
  useEffect(() => {
    if (!localCacheService.isFileSystemAccessSupported()) return;
    const checkCache = async () => {
      try {
        const result = await localCacheService.validateCacheConfig();
        if (!result.valid) {
          if (result.notConfigured) setCacheInvalid('not-configured');
          else if (result.needReauth) setCacheInvalid('need-reauth');
          else if (result.needReselect) setCacheInvalid('invalid');
        } else {
          setCacheInvalid(false);
        }
      } catch (e) { console.warn('缓存验证出错:', e); }
    };
    const handleVisibility = () => { if (document.visibilityState === 'visible') checkCache(); };
    const handleCacheChange = (e) => { setCacheInvalid(e.detail?.valid ? false : 'invalid'); };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('cache-config-changed', handleCacheChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('cache-config-changed', handleCacheChange);
    };
  }, []);

  // 项目加载后验证缓存（供 loadProjectData 调用）
  const validateAfterLoad = useCallback(async () => {
    if (!localCacheService.isFileSystemAccessSupported()) return;
    try {
      const cacheResult = await localCacheService.validateCacheConfig();
      if (!cacheResult.valid) {
        setCacheInvalid(cacheResult.notConfigured ? 'not-configured' : cacheResult.needReauth ? 'need-reauth' : cacheResult.needReselect ? 'invalid' : false);
      } else {
        setCacheInvalid(false);
      }
    } catch (e) { console.warn('缓存验证出错:', e); }
  }, []);

  return {
    cacheInvalid, setCacheInvalid,
    cacheReconfiguring, setCacheReconfiguring,
    showCacheConfirm, setShowCacheConfirm,
    validateAfterLoad,
  };
}
